import { ChevronRight, Search, Sparkles, Wrench } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormState, useWatch, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '../../Shared/Button/Button';
import { Input } from '../../Shared/Input/Input';
import { Switch } from '../../Shared/Switch/Switch';
import { MemoryCapture } from '../MemoryCapture/MemoryCapture';
import { RecommendedSetupChanges, RecommendedSetupDialog } from './RecommendedSetupDialog';
import { RemoteMcpRecommendedSetup } from '@/Domain/api/Action/dto/RemoteMCP.dto';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import {
  MCP_TOOL_MEMORY_CAPTURE_LIMIT,
  MCP_TOOL_THINKING_MESSAGE_LIMIT,
  RemoteMcpForm,
} from '@/Domain/hooks/Action/RemoteMcp/useRemoteMcpActionForm.hook';
import { MODAL_NAMES } from '@/Domain/store/modal.store';
import { cn } from '@/Domain/utils/cn';

type Props = {
  form: UseFormReturn<RemoteMcpForm>;
  isHidden: boolean;
  name: 'allowed_tools';
  /** Absent for servers Quickchat has not curated. */
  recommendedSetup?: RemoteMcpRecommendedSetup | null;
  /** Per tool, the message the visitor sees when the field below is left empty. */
  thinkingDefaults?: Record<string, string>;
};

type MemoryRows = { json_path: string; metadata_key: string }[] | null | undefined;

const sameMemory = (a: MemoryRows, b: MemoryRows) => {
  const left = (a ?? []).map((r) => `${r.json_path}|${r.metadata_key}`);
  const right = (b ?? []).map((r) => `${r.json_path}|${r.metadata_key}`);
  return left.length === right.length && left.every((v, i) => v === right[i]);
};

const SEARCH_THRESHOLD = 6;

export const RemoteMcpActionTools = ({
  form,
  isHidden,
  name,
  recommendedSetup,
  thinkingDefaults,
}: Props) => {
  const { t } = useTranslation('remote_mcp');
  const { fields } = useFieldArray({ control: form.control, name });
  const watched = useWatch({ control: form.control, name }) ?? [];
  const defaultToolIsActive = useWatch({ control: form.control, name: 'default_tool_is_active' });
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const recommendedTriggerRef = useRef<HTMLButtonElement>(null);
  const { errors } = useFormState({ control: form.control, name });
  const toolErrors = errors.allowed_tools;
  const firstErrorToolIndex = Array.isArray(toolErrors)
    ? toolErrors.findIndex((error) => error?.save_to_memory || error?.thinking_message)
    : -1;

  useEffect(() => {
    if (firstErrorToolIndex < 0) return;
    setQuery('');
    setExpanded(firstErrorToolIndex);
  }, [firstErrorToolIndex]);

  if (isHidden) return null;

  const total = fields.length;
  const activeCount = watched.filter((tool) => tool?.is_active).length;
  const recommended = new Set(recommendedSetup?.tools ?? []);

  const setActive = (idx: number, value: boolean) =>
    form.setValue(`${name}.${idx}.is_active`, value, { shouldDirty: true });
  const setAll = (value: boolean) =>
    form.setValue(
      name,
      watched.map((tool) => ({ ...tool, is_active: value })),
      { shouldDirty: true },
    );
  // One next state feeds both the preview and Apply, so the two cannot drift apart.
  const memoryByTool = new Map(
    (recommendedSetup?.memory ?? []).map((m) => [m.tool_name, m.save_to_memory]),
  );
  const nextTools = watched.map((tool) => {
    const memory = memoryByTool.get(tool.name);
    return {
      ...tool,
      is_active: recommended.has(tool.name),
      ...(memory ? { save_to_memory: memory } : {}),
    };
  });
  const changes: RecommendedSetupChanges = {
    toEnable: nextTools.filter((t, i) => t.is_active && !watched[i]?.is_active).map((t) => t.name),
    toDisable: nextTools.filter((t, i) => !t.is_active && watched[i]?.is_active).map((t) => t.name),
    memoryAdded: [],
    memoryReplaced: [],
    // The recommendation must also hold for tools the server exposes later.
    disableFutureTools: !!defaultToolIsActive,
    summaryKey: recommendedSetup?.summary_key ?? '',
  };
  nextTools.forEach((next, i) => {
    const current = watched[i];
    if (!current || sameMemory(current.save_to_memory, next.save_to_memory)) return;
    const keys = (next.save_to_memory ?? []).map((r) => r.metadata_key);
    const entry = { toolName: next.name, keys };
    if ((current.save_to_memory ?? []).length > 0) changes.memoryReplaced.push(entry);
    else changes.memoryAdded.push(entry);
  });

  const applyRecommended = () => {
    form.setValue(name, nextTools, { shouldDirty: true });
    if (changes.disableFutureTools) {
      form.setValue('default_tool_is_active', false, { shouldDirty: true });
    }
    setConfirmOpen(false);
  };

  const captureCount = (idx: number) => watched[idx]?.save_to_memory?.length ?? 0;

  const rows = fields
    .map((field, idx) => ({
      id: field.id,
      idx,
      toolName: watched[idx]?.name ?? '',
      active: !!watched[idx]?.is_active,
    }))
    .filter((row) => row.toolName.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <section className="flex flex-col gap-3" data-testid="remote-mcp-tools">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <h3 className="text-sm font-medium text-foreground">{t('tools.title')}</h3>
          <p className="text-sm text-muted-foreground">
            {total === 0
              ? t('tools.none_yet')
              : t('tools.enabled_count', { active: activeCount, total })}
          </p>
        </div>
        {total > 0 && (
          <div className="flex flex-wrap items-center justify-end gap-1">
            {recommended.size > 0 && (
              <Button
                type="button"
                ref={recommendedTriggerRef}
                variant="ghost"
                size="sm"
                onClick={() => setConfirmOpen(true)}
                data-testid="remote-mcp-tools-use-recommended"
              >
                <Sparkles className="size-3.5" />
                {t('tools.use_recommended')}
              </Button>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={() => setAll(true)}>
              {t('tools.enable_all')}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setAll(false)}>
              {t('tools.disable_all')}
            </Button>
          </div>
        )}
      </div>

      {total === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border px-6 py-8 text-center">
          <span className="flex size-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Wrench className="size-4" />
          </span>
          <p className="text-sm text-muted-foreground">{t('tools.empty')}</p>
        </div>
      ) : (
        <>
          {total > SEARCH_THRESHOLD && (
            <Input
              startIcon={<Search className="size-4 text-foreground-subtle" />}
              placeholder={t('tools.search_placeholder')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              data-testid="remote-mcp-tools-search"
            />
          )}
          <div className="max-h-80 overflow-y-auto rounded-xl border border-border">
            {rows.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                {t('tools.no_matches')}
              </p>
            ) : (
              rows.map(({ id, idx, toolName, active }) => (
                <div key={id} className="border-b border-border last:border-b-0">
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <button
                      type="button"
                      onClick={() => setExpanded(expanded === idx ? null : idx)}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      aria-expanded={expanded === idx}
                      data-testid={`remote-mcp-tool-toggle-${toolName}`}
                    >
                      <ChevronRight
                        className={cn(
                          'size-3.5 shrink-0 text-foreground-subtle transition-transform',
                          expanded === idx && 'rotate-90',
                        )}
                      />
                      <span className="truncate font-mono text-sm text-foreground">{toolName}</span>
                      {captureCount(idx) > 0 && (
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          {t('tools.memory_count', { count: captureCount(idx) })}
                        </span>
                      )}
                    </button>
                    <Switch
                      aria-label={toolName}
                      checked={active}
                      onCheckedChange={(value) => setActive(idx, value)}
                    />
                  </div>
                  {expanded === idx && (
                    <div className="space-y-4 border-t border-border bg-muted/30 px-4 py-3">
                      <FormField
                        name={`${name}.${idx}.thinking_message`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm">
                              {t('tools.thinking_message.label')}
                            </FormLabel>
                            <FormDescription className="text-xs">
                              {t('tools.thinking_message.description')}
                            </FormDescription>
                            <FormControl>
                              <Input
                                maxLength={MCP_TOOL_THINKING_MESSAGE_LIMIT}
                                value={field.value ?? ''}
                                onChange={(event) => field.onChange(event.target.value || null)}
                                placeholder={
                                  // "" is a real default (the tool runs silently), not a missing one.
                                  typeof thinkingDefaults?.[toolName] === 'string'
                                    ? thinkingDefaults[toolName] ||
                                      t('tools.thinking_message.silent_placeholder')
                                    : t('tools.thinking_message.placeholder')
                                }
                                data-testid={`remote-mcp-tool-thinking-${toolName}`}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <MemoryCapture
                        form={form}
                        name={`${name}.${idx}.save_to_memory` as never}
                        compact
                        description={t('tools.memory_description')}
                        hintVariant="prompt"
                        maxRows={MCP_TOOL_MEMORY_CAPTURE_LIMIT}
                        testIdPrefix={`remote-mcp-tool-memory-${toolName}`}
                      />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
      <RecommendedSetupDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={applyRecommended}
        changes={changes}
        containerNode={document.getElementById(MODAL_NAMES.REMOTE_MCP_DIALOG)}
        triggerRef={recommendedTriggerRef}
      />
      <FormField
        name="default_tool_is_active"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-muted/40 p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-sm">{t('form.default_tool_is_active_label')}</FormLabel>
                <FormDescription className="text-xs">
                  {t('form.default_tool_is_active_description')}
                </FormDescription>
              </div>
              <FormControl className="self-center">
                <Switch
                  data-testid="remote-mcp-default-tool-active"
                  checked={field.value}
                  onCheckedChange={(value) => field.onChange(value)}
                />
              </FormControl>
            </div>
          </FormItem>
        )}
      />
    </section>
  );
};
